/**
 * Drawing the laser trail (SRS C4).
 *
 * A soft wide glow with a brighter line down the middle, both fading from the tail to
 * the head, and a solid dot where the hand is. That reads as a beam on a projector at
 * the back of a classroom, where a thin line would be lost.
 *
 * The trail is drawn in SCREEN pixels, not canvas units: a pointer is a thing on the
 * screen, and it should be the same size to the class whatever the zoom is.
 */

import { canvasToScreen, type Point, type Viewport } from '@opal/shared';

import { LASER_FADE_MS, LASER_SCREEN_WIDTH, type LaserMark } from './laser-session.js';
import { cssColor } from './palette.js';

const BEAM_TOKEN = '--laser';
const BEAM_FALLBACK = '#ff2d55';
const GLOW_TOKEN = '--laser-glow';
const GLOW_FALLBACK = 'rgba(255, 45, 85, 0.28)';

/** How much wider the glow is than the beam inside it. */
const GLOW_SCALE = 2.6;

/** How much of its life a mark spends at full strength before it starts to go. */
const HOLD = 0.35;

/** How solid a mark is, from 1 when it is made to 0 when its time is up. */
export function laserAlpha(mark: LaserMark, now: number): number {
  const age = (now - mark.at) / LASER_FADE_MS;
  if (age <= HOLD) return 1;
  if (age >= 1) return 0;
  return 1 - (age - HOLD) / (1 - HOLD);
}

function screenPoints(trail: readonly LaserMark[], viewport: Viewport): Point[] {
  return trail.map((mark) => canvasToScreen(viewport, { x: mark.x, y: mark.y }));
}

/** One pass along the trail, each piece drawn at its own strength. */
function stroke(
  ctx: CanvasRenderingContext2D,
  points: readonly Point[],
  trail: readonly LaserMark[],
  now: number,
  color: string,
  width: number,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const mark = trail[index];
    if (!from || !to || !mark) continue;
    ctx.globalAlpha = laserAlpha(mark, now);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }
}

/** Draws the whole trail, tail to head, with the dot on the end. */
export function drawLaser(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  trail: readonly LaserMark[],
  now: number,
): void {
  if (trail.length === 0) return;
  const points = screenPoints(trail, viewport);
  const beam = cssColor(BEAM_TOKEN, BEAM_FALLBACK);
  const glow = cssColor(GLOW_TOKEN, GLOW_FALLBACK);

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  stroke(ctx, points, trail, now, glow, LASER_SCREEN_WIDTH * GLOW_SCALE);
  stroke(ctx, points, trail, now, beam, LASER_SCREEN_WIDTH);

  // docs/06-UI-SPEC.md §15 — the pointer itself is a small dot where the hand is.
  const head = points[points.length - 1];
  const last = trail[trail.length - 1];
  if (head && last) {
    ctx.globalAlpha = laserAlpha(last, now);
    ctx.fillStyle = beam;
    ctx.beginPath();
    ctx.arc(head.x, head.y, LASER_SCREEN_WIDTH * 0.75, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
